import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, X, ArrowRight, ShieldCheck } from "lucide-react";
import { useApp } from "../context/AppContext";
import { api, resolveImage } from "../lib/api";
import { toast } from "sonner";

export default function Cart() {
    const { cart, updateQty, removeFromCart, cartSubtotal, user } = useApp();
    const navigate = useNavigate();
    const [email, setEmail] = useState(user?.email || "");
    const [discountCode, setDiscountCode] = useState("");
    const [discountAmount, setDiscountAmount] = useState(0);
    const [validatedCode, setValidatedCode] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [address, setAddress] = useState({ line1: "", city: "", postal: "", country: "Italia" });

    const applyDiscount = async () => {
        if (!discountCode) return;
        try {
            const r = await api.post("/discounts/validate", { code: discountCode, subtotal: cartSubtotal });
            setDiscountAmount(r.data.amount);
            setValidatedCode(r.data.code);
            toast.success(`Codice applicato · -€${r.data.amount.toFixed(2)}`);
        } catch (e) {
            setDiscountAmount(0);
            setValidatedCode("");
            toast.error(e.response?.data?.detail || "Codice non valido");
        }
    };

    const checkout = async () => {
        if (cart.length === 0) return;
        if (!email) { toast.error("Inserisci la tua email"); return; }
        setSubmitting(true);
        try {
            const r = await api.post("/checkout/session", {
                items: cart.map(it => ({ product_id: it.product_id, size: it.size, quantity: it.quantity })),
                discount_code: validatedCode || null,
                email,
                shipping_address: address,
                origin_url: window.location.origin,
            });
            window.location.href = r.data.url;
        } catch (e) {
            toast.error(e.response?.data?.detail || "Checkout non riuscito");
            setSubmitting(false);
        }
    };

    const total = Math.max(cartSubtotal - discountAmount, 0);

    if (cart.length === 0) {
        return (
            <div className="px-6 md:px-12 lg:px-24 py-32 text-center" data-testid="cart-empty-page">
                <div className="overline mb-3">Carrello</div>
                <h1 className="font-display text-5xl text-[color:var(--ms-charcoal)]">È vuoto per ora.</h1>
                <p className="text-[color:var(--ms-muted)] mt-3 text-sm">Scopri i nuovi arrivi e aggiungi il tuo primo capo Mini Style.</p>
                <Link to="/shop" className="inline-block mt-10 bg-[color:var(--ms-charcoal)] text-[color:var(--ms-warm-white)] px-8 py-4 text-xs tracking-[0.22em] font-medium hover:bg-[color:var(--ms-sage-dark)] transition-colors" data-testid="cart-empty-shop">
                    SCOPRI LA COLLEZIONE
                </Link>
            </div>
        );
    }

    return (
        <div className="px-6 md:px-12 lg:px-24 py-12" data-testid="cart-page">
            <div className="overline mb-3">Checkout</div>
            <h1 className="font-display text-5xl tracking-tight text-[color:var(--ms-charcoal)] mb-12">Il tuo carrello</h1>

            <div className="grid lg:grid-cols-[1.5fr_1fr] gap-12">
                <div>
                    <ul className="divide-y divide-[color:var(--ms-border)] border-y border-[color:var(--ms-border)]">
                        {cart.map((it, idx) => (
                            <li key={idx} className="flex gap-5 py-6" data-testid={`cart-row-${idx}`}>
                                <img src={resolveImage(it.image)} alt={it.title} className="w-24 h-32 object-cover bg-[color:var(--ms-cream)]" />
                                <div className="flex-1">
                                    <div className="overline">{it.brand}</div>
                                    <Link to={`/product/${it.slug}`} className="text-[color:var(--ms-charcoal)] font-medium link-underline">{it.title}</Link>
                                    <div className="text-xs text-[color:var(--ms-muted)] mt-1">Taglia {it.size}</div>
                                    <div className="flex items-center gap-2 border border-[color:var(--ms-border)] w-fit mt-3">
                                        <button onClick={() => updateQty(idx, it.quantity - 1)} className="px-2 py-1 hover:bg-[color:var(--ms-cream)]"><Minus size={12} /></button>
                                        <span className="text-sm w-6 text-center">{it.quantity}</span>
                                        <button onClick={() => updateQty(idx, it.quantity + 1)} className="px-2 py-1 hover:bg-[color:var(--ms-cream)]"><Plus size={12} /></button>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-[color:var(--ms-charcoal)] font-medium">€{(it.price * it.quantity).toFixed(2)}</div>
                                    <button onClick={() => removeFromCart(idx)} className="text-[color:var(--ms-muted)] hover:text-[color:var(--ms-terracotta)] mt-3" data-testid={`cart-remove-row-${idx}`}><X size={14} /></button>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>

                <aside className="panel p-8 self-start space-y-5">
                    <h2 className="font-display text-2xl text-[color:var(--ms-charcoal)]">Riepilogo Ordine</h2>

                    <div className="space-y-3">
                        <label className="overline block">Contatto</label>
                        <input
                            type="email"
                            required
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            placeholder="tua@email.it"
                            className={inputCls}
                            data-testid="checkout-email"
                        />
                    </div>

                    <div className="space-y-3">
                        <label className="overline block">Spedisci a</label>
                        <input value={address.line1} onChange={e => setAddress({ ...address, line1: e.target.value })} placeholder="Indirizzo" className={inputCls} data-testid="checkout-address" />
                        <div className="grid grid-cols-2 gap-3">
                            <input value={address.city} onChange={e => setAddress({ ...address, city: e.target.value })} placeholder="Città" className={inputCls} data-testid="checkout-city" />
                            <input value={address.postal} onChange={e => setAddress({ ...address, postal: e.target.value })} placeholder="CAP" className={inputCls} data-testid="checkout-postal" />
                        </div>
                        <input value={address.country} onChange={e => setAddress({ ...address, country: e.target.value })} placeholder="Paese" className={inputCls} data-testid="checkout-country" />
                    </div>

                    <div className="space-y-3">
                        <label className="overline block">Codice Sconto</label>
                        <div className="flex gap-2">
                            <input value={discountCode} onChange={e => setDiscountCode(e.target.value)} placeholder="WELCOME10" className={`${inputCls} flex-1`} data-testid="checkout-discount-input" />
                            <button onClick={applyDiscount} className="border border-[color:var(--ms-charcoal)] text-[color:var(--ms-charcoal)] px-4 text-xs tracking-[0.2em] uppercase hover:bg-[color:var(--ms-charcoal)] hover:text-[color:var(--ms-warm-white)] transition-colors" data-testid="checkout-discount-apply">Applica</button>
                        </div>
                    </div>

                    <div className="border-t border-[color:var(--ms-border)] pt-5 space-y-2 text-sm">
                        <div className="flex justify-between text-[color:var(--ms-muted)]">
                            <span>Subtotale</span><span>€{cartSubtotal.toFixed(2)}</span>
                        </div>
                        {discountAmount > 0 && (
                            <div className="flex justify-between text-[color:var(--ms-terracotta)]">
                                <span>Sconto ({validatedCode})</span><span>-€{discountAmount.toFixed(2)}</span>
                            </div>
                        )}
                        <div className="flex justify-between text-[color:var(--ms-muted)]">
                            <span>Spedizione</span><span>Calcolata al checkout</span>
                        </div>
                        <div className="flex justify-between text-[color:var(--ms-charcoal)] text-lg font-medium pt-3 border-t border-[color:var(--ms-border)]" data-testid="cart-total">
                            <span>Totale</span><span>€{total.toFixed(2)}</span>
                        </div>
                    </div>

                    <button onClick={checkout} disabled={submitting} className="w-full bg-[color:var(--ms-charcoal)] text-[color:var(--ms-warm-white)] py-4 text-xs tracking-[0.22em] font-medium hover:bg-[color:var(--ms-sage-dark)] transition-colors disabled:opacity-50 flex items-center justify-center gap-2" data-testid="checkout-submit-btn">
                        {submitting ? "REINDIRIZZAMENTO…" : <>PAGAMENTO SICURO <ArrowRight size={14} /></>}
                    </button>
                    <p className="text-[10px] text-[color:var(--ms-muted)] text-center tracking-wider flex items-center justify-center gap-1.5"><ShieldCheck size={12} /> Pagamento sicuro Stripe · Apple Pay · Google Pay · Visa · Mastercard</p>
                </aside>
            </div>
        </div>
    );
}

const inputCls = "w-full bg-[color:var(--ms-warm-white)] border border-[color:var(--ms-border)] focus:border-[color:var(--ms-charcoal)] px-3 py-3 text-sm outline-none text-[color:var(--ms-charcoal)]";
